// Callouts (a highlighted aside), a container node like the toggle: its body is
// ordinary editor blocks, so it is written like the rest of the document.
//
// ## What goes inside
//
// A callout is an aside, so its body is paragraphs and bullet / ordered lists
// only (`calloutContent`). Headings, tables, code, diagrams, toggles belong to
// the main text. Nested lists cannot be stopped by the schema, so Tab
// (`sinkListItem`) is disabled inside a callout (`addKeyboardShortcuts`).
//
// ## Canonical markdown (GitHub's alert syntax)
//
//     > [!NOTE] Title
//     > A paragraph.
//     >
//     > - item
//     > - item
//
// Kinds: `NOTE` / `TIP` / `IMPORTANT` / `WARNING` / `CAUTION` (lowercase in the
// node). The title is optional (`> [!NOTE]` alone). GitHub draws the form
// without a title; the titled form is Obsidian's. Either way it is a plain
// quote that agents and people can read.
//
// ## Anything else stays a blockquote
//
// A body with something other than paragraphs and lists makes the tokenizer
// return undefined, and marked's blockquote keeps the `> [!NOTE] ...` line as a
// paragraph, so the bytes do not change.
//
// The old fenced JSON callout is not read here; the data conversion rewrites
// it into this syntax.

import type {
  JSONContent,
  MarkdownParseHelpers,
  MarkdownRendererHelpers,
  MarkdownToken,
} from "@tiptap/core";
import { mergeAttributes, Node } from "@tiptap/core";
import { Selection } from "@tiptap/pm/state";
import { neverMatchingTokenizer } from "./details";
import { Lines } from "./lines";
import { renderBlockSiblings } from "./render-blocks";

/** The five alert kinds. The screen gives each a color. */
export const CALLOUT_KINDS = [
  "note",
  "tip",
  "important",
  "warning",
  "caution",
] as const;
export type CalloutKind = (typeof CALLOUT_KINDS)[number];

/** The first line: `> [!NOTE]`, then one space and the title (optional). */
const HEAD_RE = /^> \[!(NOTE|TIP|IMPORTANT|WARNING|CAUTION)\](?: (.*))?$/;
/** A following line: `>` alone (blank) or `> ` + content. */
const BODY_RE = /^>(?: (.*))?$/;

/** marked block tokens allowed in the body. */
const ALLOWED_TOKEN_TYPES = new Set(["paragraph", "text", "list", "space"]);

interface CalloutScan {
  kind: CalloutKind;
  title: string;
  /** The body without `> ` (with a trailing newline; "" when empty). */
  content: string;
  /** Characters consumed. */
  rawLength: number;
}

/**
 * Whether `src` starts with a callout, scanning lines. It ends at the first line
 * not starting with `>` (no lazy continuation: a line that forgot its `>` is a
 * paragraph after the callout).
 */
export function scanCallout(src: string): CalloutScan | undefined {
  const lines = new Lines(src);
  const head = HEAD_RE.exec(lines.at(0) ?? "");
  if (!head) return undefined;

  const body: string[] = [];
  let i = 1;
  for (let line = lines.at(i); line !== undefined; line = lines.at(++i)) {
    const match = BODY_RE.exec(line);
    if (!match) break;
    body.push(match[1] ?? "");
  }
  const consumed = lines.join(0, i);
  const content = body.join("\n").replace(/^\n+|\n+$/g, "");
  return {
    kind: head[1].toLowerCase() as CalloutKind,
    title: head[2] ?? "",
    content: content === "" ? "" : `${content}\n`,
    rawLength: consumed.length + (src.length > consumed.length ? 1 : 0),
  };
}

/** `[!NOTE]` (uppercase). */
function kindLabel(kind: unknown): string {
  return String(kind ?? "note").toUpperCase();
}

/** The body markdown as `> ` lines; blank lines become `>`. */
function quoteLines(content: string): string {
  return content
    .split("\n")
    .map((line) => (line === "" ? ">" : `> ${line}`))
    .join("\n");
}

export const Callout = Node.create({
  name: "callout",
  group: "block",
  content: "calloutTitle calloutContent",
  defining: true,
  isolating: true,
  // Above listItem's default (100) so the Tab handler below runs first; below
  // paragraph's 1000 so the default block type of `block+` stays the paragraph.
  priority: 101,

  addAttributes() {
    return {
      kind: {
        default: "note" as CalloutKind,
        parseHTML: (el) => el.getAttribute("data-kind") ?? "note",
        renderHTML: (attrs) => ({ "data-kind": attrs.kind }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-type="callout"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      mergeAttributes(HTMLAttributes, { "data-type": "callout" }),
      0,
    ];
  },

  addKeyboardShortcuts() {
    return {
      // No deeper list levels inside a callout.
      Tab: () => {
        const { $from } = this.editor.state.selection;
        let inCallout = false;
        let inList = false;
        for (let d = $from.depth; d > 0; d--) {
          const name = $from.node(d).type.name;
          if (name === "listItem") inList = true;
          if (name === "callout") inCallout = true;
        }
        return inCallout && inList;
      },
      // Enter in the title moves to the start of the body (the title is one line).
      Enter: () => {
        const { state, view } = this.editor;
        const { $head } = state.selection;
        if ($head.parent.type.name !== "calloutTitle") return false;
        const contentStart = $head.after() + 1;
        const tr = state.tr.setSelection(
          Selection.near(state.doc.resolve(contentStart), 1),
        );
        view.dispatch(tr.scrollIntoView());
        return true;
      },
    };
  },

  markdownTokenName: "callout",
  markdownTokenizer: {
    name: "callout",
    level: "block" as const,
    start(src: string) {
      const match = src.match(
        /^> \[!(?:NOTE|TIP|IMPORTANT|WARNING|CAUTION)\]/m,
      );
      return match ? (match.index ?? -1) : -1;
    },
    tokenize(src: string, _tokens: MarkdownToken[], lexer) {
      const scan = scanCallout(src);
      if (!scan) return undefined;

      // As in details.ts: lexing re-entered from a list item returns
      // paragraphs as block-level `text`, read as `paragraph`.
      const tokens = lexer
        .blockTokens(scan.content)
        .map((t) =>
          t.type === "text"
            ? ({ ...t, type: "paragraph" } as MarkdownToken)
            : t,
        );
      // Anything other than paragraphs and lists: not a callout (stays a blockquote).
      if (tokens.some((t) => !ALLOWED_TOKEN_TYPES.has(t.type ?? ""))) {
        return undefined;
      }

      return {
        type: "callout",
        raw: src.slice(0, scan.rawLength),
        kind: scan.kind,
        title: scan.title,
        tokens,
      };
    },
  },

  parseMarkdown(token: MarkdownToken, h: MarkdownParseHelpers) {
    const parseBlockChildren = h.parseBlockChildren ?? h.parseChildren;
    const title = (token.title as string | undefined) ?? "";
    let content = parseBlockChildren(token.tokens ?? []);
    // `calloutContent` is `block+`: an empty body is one empty paragraph.
    if (content.length === 0) {
      content = [h.createNode("paragraph", undefined, [])];
    }
    return h.createNode("callout", { kind: token.kind ?? "note" }, [
      h.createNode(
        "calloutTitle",
        undefined,
        title.length > 0 ? [h.createTextNode(title)] : [],
      ),
      h.createNode("calloutContent", undefined, content),
    ]);
  },

  renderMarkdown(node: JSONContent, h: MarkdownRendererHelpers) {
    const children = node.content ?? [];
    const renderChild = h.renderChild;
    const render = (child: JSONContent | undefined, index: number) =>
      child === undefined
        ? ""
        : (renderChild?.(child, index) ?? h.renderChildren([child]));

    const title = render(children[0], 0);
    const head = `> [!${kindLabel(node.attrs?.kind)}]${title ? ` ${title}` : ""}`;
    // The body's own leading / trailing line breaks are dropped (as in details.ts).
    const content = render(children[1], 1).replace(/^\n+|\n+$/g, "");
    return content === "" ? head : `${head}\n${quoteLines(content)}`;
  },
});

/**
 * The title: `text*`, written as plain text (reading `**bold**` as strong would
 * change the bytes).
 */
export const CalloutTitle = Node.create({
  name: "calloutTitle",
  content: "text*",
  marks: "",
  defining: true,
  isolating: true,

  parseHTML() {
    return [{ tag: 'div[data-type="calloutTitle"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      mergeAttributes(HTMLAttributes, { "data-type": "calloutTitle" }),
      0,
    ];
  },

  markdownTokenName: "calloutTitle",
  markdownTokenizer: neverMatchingTokenizer("calloutTitle"),

  parseMarkdown(token: MarkdownToken, h: MarkdownParseHelpers) {
    const text = (token.text as string | undefined) ?? "";
    return h.createNode(
      "calloutTitle",
      undefined,
      text.length > 0 ? [h.createTextNode(text)] : [],
    );
  },

  renderMarkdown(node: JSONContent) {
    return (node.content ?? []).map((child) => child.text ?? "").join("");
  },
});

/** The body: paragraphs and one-level lists, joined like the doc. */
export const CalloutContent = Node.create({
  name: "calloutContent",
  content: "(paragraph | bulletList | orderedList)+",
  defining: true,

  parseHTML() {
    return [{ tag: 'div[data-type="calloutContent"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      mergeAttributes(HTMLAttributes, { "data-type": "calloutContent" }),
      0,
    ];
  },

  markdownTokenName: "calloutContent",
  markdownTokenizer: neverMatchingTokenizer("calloutContent"),

  parseMarkdown(token: MarkdownToken, h: MarkdownParseHelpers) {
    const parseBlockChildren = h.parseBlockChildren ?? h.parseChildren;
    return h.createNode(
      "calloutContent",
      undefined,
      parseBlockChildren(token.tokens ?? []),
    );
  },

  renderMarkdown(node: JSONContent, h: MarkdownRendererHelpers) {
    return renderBlockSiblings(node.content ?? [], h);
  },
});
